import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CertificateClassStep } from '../Certificates/CertificateClassStep';
import {
  CertificateDetailsStep,
  hasValidCertificateDetails,
} from '../Certificates/CertificateDetailsStep';
import { CertificatePreviewStep } from '../Certificates/CertificatePreviewStep';
import {
  CertificateGradesStep,
  haveCompleteCertificateGrades,
} from '../Certificates/CertificateGradesStep';
import { CertificateBatchProgress } from '../Certificates/CertificateBatchProgress';
import { CertificateDraftBatchList } from '../Certificates/CertificateDraftBatchList';
import { CertificateManualUpload } from '../Certificates/CertificateManualUpload';
import { useCertificateBatch } from '../Certificates/useCertificateBatch';
import { certificateApi } from '../../services/certificateApi';
import './AdminOperations.css';
import './CertificateWizard.css';
import {
  getClases,
  obtenerListadoMatriculasAlumnos,
  subirCertificado,
} from './constants';

const INITIAL_WIZARD_FORM = {
  id_clase: '',
  tipo_emision: 'individual',
  id_alumno: '',
  capacitacion: '',
  fecha_capacitacion: '',
  horas: '',
  fecha_expedicion: '',
};

export const buildCertificatePayload = (
  form,
  recipients = [],
  grades = {},
  certificateNames = {},
) => {
  const isSelectedEmission = form.tipo_emision === 'seleccionada';
  const payload = {
    id_clase: Number(form.id_clase),
    tipo_emision: isSelectedEmission ? 'masiva' : form.tipo_emision,
    capacitacion: form.capacitacion.trim(),
    fecha_capacitacion: form.fecha_capacitacion.trim(),
    horas: Number(form.horas),
    fecha_expedicion: form.fecha_expedicion,
    calificaciones: recipients.map((student) => ({
      id_alumno: Number(student.id_alumno),
      calificacion: Number(grades[String(student.id_alumno)]),
    })),
    nombres_alumnos: recipients.map((student) => ({
      id_alumno: Number(student.id_alumno),
      nombre_alumno: String(
        certificateNames[String(student.id_alumno)] ??
          student.nombre_alumno ??
          '',
      ).trim(),
    })),
  };

  if (form.tipo_emision === 'individual') {
    payload.id_alumno = Number(form.id_alumno);
  } else if (isSelectedEmission) {
    payload.id_alumnos = recipients.map((student) =>
      Number(student.id_alumno),
    );
  }

  return payload;
};

const certificatePayloadKey = (payload) => JSON.stringify(payload);

const studentLabel = (student) =>
  student?.matricula_alumno
    ? `${student.nombre_alumno} (${student.matricula_alumno})`
    : student?.nombre_alumno || '';

export const CrearCertificado = ({
  onPreviewRequest,
  onConfirmPublication,
  onGeneratedArtifactsInvalidated,
}) => {
  const [wizardForm, setWizardForm] = useState(INITIAL_WIZARD_FORM);
  const [gradesByStudent, setGradesByStudent] = useState({});
  const [certificateNamesByStudent, setCertificateNamesByStudent] = useState({});
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classStudents, setClassStudents] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [wizardError, setWizardError] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const {
    batch,
    running: batchRunning,
    error: batchError,
    start: startBatch,
    load: loadBatch,
    resume: resumeBatch,
    retryFailed: retryFailedBatch,
    publish: publishBatch,
    cancel: cancelBatch,
    cancelDraftIssuance,
    clear: clearBatch,
  } = useCertificateBatch();
  const [draftListRefreshKey, setDraftListRefreshKey] = useState(0);
  const previewRequestId = useRef(0);
  const previewUrlRef = useRef(null);
  const wizardMountedRef = useRef(false);

  const [manualStudents, setManualStudents] = useState([]);
  const [cargandoAlumnos, setCargandoAlumnos] = useState(true);
  const [manualLoadError, setManualLoadError] = useState('');

  const revokePreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const invalidateGeneratedArtifacts = () => {
    previewRequestId.current += 1;
    revokePreviewUrl();
    setPreview(null);
    setPreviewLoading(false);
    setPreviewError('');
    if (typeof onGeneratedArtifactsInvalidated === 'function') {
      onGeneratedArtifactsInvalidated();
    }
  };

  useEffect(() => {
    wizardMountedRef.current = true;

    return () => {
      wizardMountedRef.current = false;
      previewRequestId.current += 1;
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let active = true;

    const loadClasses = async () => {
      try {
        const response = await axios.get(getClases);
        const list = response.data?.data ?? response.data ?? [];
        if (active) {
          setClasses(Array.isArray(list) ? list : []);
        }
      } catch (error) {
        console.error('Error al cargar clases:', error);
        if (active) {
          setClasses([]);
          setWizardError('No fue posible cargar las clases disponibles.');
        }
      } finally {
        if (active) {
          setLoadingClasses(false);
        }
      }
    };

    loadClasses();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    if (!wizardForm.id_clase) {
      setClassStudents([]);
      setLoadingStudents(false);
      return () => {
        active = false;
      };
    }

    setLoadingStudents(true);
    setWizardError('');

    certificateApi
      .listStudents(wizardForm.id_clase)
      .then((students) => {
        if (active) {
          const nextStudents = Array.isArray(students) ? students : [];
          setClassStudents(nextStudents);
          setCertificateNamesByStudent(
            Object.fromEntries(
              nextStudents.map((student) => [
                String(student.id_alumno),
                student.nombre_alumno || '',
              ]),
            ),
          );
        }
      })
      .catch((error) => {
        console.error('Error al cargar alumnos de la clase:', error);
        if (active) {
          setClassStudents([]);
          setWizardError('No fue posible cargar los alumnos de esta clase.');
        }
      })
      .finally(() => {
        if (active) {
          setLoadingStudents(false);
        }
      });

    return () => {
      active = false;
    };
  }, [wizardForm.id_clase]);

  useEffect(() => {
    let active = true;

    const loadManualStudents = async () => {
      try {
        const response = await axios.get(obtenerListadoMatriculasAlumnos);
        const list = response.data?.data?.alumnos ?? [];
        if (active) {
          setManualStudents(Array.isArray(list) ? list : []);
          setManualLoadError('');
        }
      } catch (error) {
        console.error('Error al cargar alumnos:', error);
        if (active) {
          setManualStudents([]);
          setManualLoadError('No fue posible cargar la lista de alumnos.');
        }
      } finally {
        if (active) {
          setCargandoAlumnos(false);
        }
      }
    };

    loadManualStudents();
    return () => {
      active = false;
    };
  }, []);

  const handleClassChange = (idClass) => {
    setWizardForm((current) => ({
      ...current,
      id_clase: idClass,
      id_alumno: '',
    }));
    setClassStudents([]);
    setGradesByStudent({});
    setCertificateNamesByStudent({});
    setSelectedStudentIds([]);
    setWizardError('');
    invalidateGeneratedArtifacts();
  };

  const handleModeChange = (mode) => {
    setWizardForm((current) => ({
      ...current,
      tipo_emision: mode,
      id_alumno: '',
    }));
    setGradesByStudent({});
    setSelectedStudentIds([]);
    invalidateGeneratedArtifacts();
  };

  const handleStudentChange = (idStudent) => {
    setWizardForm((current) => ({ ...current, id_alumno: idStudent }));
    setGradesByStudent({});
    invalidateGeneratedArtifacts();
  };

  const handleDetailsChange = (field, value) => {
    setWizardForm((current) => ({ ...current, [field]: value }));
    invalidateGeneratedArtifacts();
  };

  const handleGradeChange = (idStudent, value) => {
    setGradesByStudent((current) => ({
      ...current,
      [String(idStudent)]: value,
    }));
    invalidateGeneratedArtifacts();
  };

  const handleCertificateNameChange = (idStudent, value) => {
    setCertificateNamesByStudent((current) => ({
      ...current,
      [String(idStudent)]: value,
    }));
    invalidateGeneratedArtifacts();
  };

  const handleSelectedStudentsChange = (studentIds) => {
    setSelectedStudentIds(studentIds);
    invalidateGeneratedArtifacts();
  };

  const selectedClassExists = classes.some(
    (classItem) => String(classItem.id_clase) === wizardForm.id_clase,
  );
  const selectedStudentIsEnrolled = classStudents.some(
    (student) => String(student.id_alumno) === wizardForm.id_alumno,
  );
  const selectedStudent = classStudents.find(
    (student) => String(student.id_alumno) === wizardForm.id_alumno,
  );
  const recipients =
    wizardForm.tipo_emision === 'masiva'
      ? classStudents
      : wizardForm.tipo_emision === 'seleccionada'
        ? classStudents.filter((student) =>
            selectedStudentIds.includes(String(student.id_alumno)),
          )
        : selectedStudent
          ? [selectedStudent]
          : [];
  const handleRemoveRecipient = (idStudent) => {
    const remainingIds = recipients
      .filter((student) => String(student.id_alumno) !== String(idStudent))
      .map((student) => String(student.id_alumno));
    if (remainingIds.length === 0) {
      return;
    }
    setWizardForm((current) => ({
      ...current,
      tipo_emision: 'seleccionada',
      id_alumno: '',
    }));
    setSelectedStudentIds(remainingIds);
    invalidateGeneratedArtifacts();
  };
  const recipientsAreValid =
    selectedClassExists &&
    !loadingStudents &&
    classStudents.length > 0 &&
    (wizardForm.tipo_emision === 'masiva' ||
      (wizardForm.tipo_emision === 'seleccionada' && recipients.length > 0) ||
      selectedStudentIsEnrolled);
  const wizardIsValid =
    recipientsAreValid && hasValidCertificateDetails(wizardForm);
  const gradesAreValid = haveCompleteCertificateGrades(
    recipients,
    gradesByStudent,
  );
  const namesAreValid =
    recipients.length > 0 &&
    recipients.every((student) => {
      const name = String(
        certificateNamesByStudent[String(student.id_alumno)] ??
          student.nombre_alumno ??
          '',
      ).trim();
      return name.length > 0 && name.length <= 255;
    });
  const canPreview = wizardIsValid && gradesAreValid && namesAreValid;
  const currentPayload = canPreview
    ? buildCertificatePayload(
        wizardForm,
        recipients,
        gradesByStudent,
        certificateNamesByStudent,
      )
    : null;
  const currentPayloadKey = currentPayload
    ? certificatePayloadKey(currentPayload)
    : '';
  const previewIsCurrent = Boolean(
    preview &&
      preview.payloadKey === currentPayloadKey &&
      !previewLoading &&
      !previewError,
  );

  const handlePreviewRequest = async (event) => {
    event.preventDefault();
    if (!canPreview) {
      return;
    }

    const payload = buildCertificatePayload(
      wizardForm,
      recipients,
      gradesByStudent,
      certificateNamesByStudent,
    );
    if (typeof onPreviewRequest === 'function') {
      onPreviewRequest(payload);
    }

    const requestId = previewRequestId.current + 1;
    previewRequestId.current = requestId;
    revokePreviewUrl();
    setPreview(null);
    setPreviewLoading(true);
    setPreviewError('');

    try {
      const pdf = await certificateApi.preview(payload);
      if (
        !wizardMountedRef.current ||
        requestId !== previewRequestId.current
      ) {
        return;
      }

      const previewUrl = URL.createObjectURL(pdf);
      if (
        !wizardMountedRef.current ||
        requestId !== previewRequestId.current
      ) {
        URL.revokeObjectURL(previewUrl);
        return;
      }

      previewUrlRef.current = previewUrl;
      setPreview({
        url: previewUrl,
        payload,
        payloadKey: certificatePayloadKey(payload),
      });
    } catch {
      if (
        wizardMountedRef.current &&
        requestId === previewRequestId.current
      ) {
        setPreviewError('No fue posible generar la vista previa.');
      }
    } finally {
      if (
        wizardMountedRef.current &&
        requestId === previewRequestId.current
      ) {
        setPreviewLoading(false);
      }
    }
  };

  const handleGenerateForReview = async () => {
    if (!previewIsCurrent || batchRunning || batch) {
      return;
    }

    if (typeof onConfirmPublication === 'function') {
      onConfirmPublication(preview.payload);
      return;
    }
    const generated = await startBatch(preview.payload);
    if (generated) {
      setDraftListRefreshKey((current) => current + 1);
    }
  };

  const handleLoadBatch = async (draft) => {
    await loadBatch(draft);
  };

  const handlePublishBatch = async () => {
    if (
      !window.confirm(
        '¿Publicar este lote? Las constancias aparecerán a los estudiantes y reemplazarán las versiones vigentes anteriores.',
      )
    ) {
      return;
    }
    const published = await publishBatch();
    if (published) {
      setDraftListRefreshKey((current) => current + 1);
    }
  };

  const handleCancelBatch = async () => {
    if (
      !window.confirm(
        '¿Cancelar este lote? Sus códigos QR dejarán de permitir la consulta del PDF.',
      )
    ) {
      return;
    }
    const cancelled = await cancelBatch();
    if (cancelled) {
      setDraftListRefreshKey((current) => current + 1);
    }
  };

  const handleCancelDraftIssuance = async (student) => {
    if (
      !window.confirm(
        `¿Quitar la constancia de ${student.nombre_alumno} de este lote? Su QR quedará cancelado.`,
      )
    ) {
      return;
    }
    const updated = await cancelDraftIssuance(student.id_emision);
    if (updated) {
      setDraftListRefreshKey((current) => current + 1);
    }
  };

  const handleCreateAnotherBatch = () => {
    clearBatch();
    invalidateGeneratedArtifacts();
    setWizardForm(INITIAL_WIZARD_FORM);
    setGradesByStudent({});
    setCertificateNamesByStudent({});
    setSelectedStudentIds([]);
    setClassStudents([]);
    setWizardError('');
    setDraftListRefreshKey((current) => current + 1);
  };

  return (
    <div className="admin-operation-page certificate-generation-page">
      <main className="admin-operation-container certificate-wizard-shell">
        <section
          className="certificate-wizard"
          aria-labelledby="certificate-wizard-title"
        >
          <header className="certificate-wizard-header">
            <p className="certificate-wizard-eyebrow">
              Documentación académica
            </p>
            <h1 id="certificate-wizard-title">Generar constancias</h1>
            <p>
              Selecciona una clase, define los destinatarios y captura los datos
              que aparecerán en las constancias.
            </p>
          </header>

          <form
            className="certificate-wizard-form"
            onSubmit={handlePreviewRequest}
            noValidate
          >
            <fieldset
              className="certificate-wizard-controls"
              disabled={batchRunning || Boolean(batch)}
            >
              <CertificateClassStep
                classes={classes}
                students={classStudents}
                idClass={wizardForm.id_clase}
                mode={wizardForm.tipo_emision}
                idStudent={wizardForm.id_alumno}
                selectedStudentIds={selectedStudentIds}
                onClassChange={handleClassChange}
                onModeChange={handleModeChange}
                onStudentChange={handleStudentChange}
                onSelectedStudentsChange={handleSelectedStudentsChange}
                loadingClasses={loadingClasses}
                loadingStudents={loadingStudents}
              />
              <CertificateDetailsStep
                value={wizardForm}
                onChange={handleDetailsChange}
              />
              <CertificateGradesStep
                students={recipients}
                grades={gradesByStudent}
                onChange={handleGradeChange}
                canPreview={canPreview}
              />
              <CertificatePreviewStep
                previewUrl={preview?.url || ''}
                loading={previewLoading}
                error={previewError}
                mode={wizardForm.tipo_emision}
                selectedStudentLabel={studentLabel(
                  selectedStudent
                    ? {
                        ...selectedStudent,
                        nombre_alumno:
                          certificateNamesByStudent[
                            String(selectedStudent.id_alumno)
                          ] || selectedStudent.nombre_alumno,
                      }
                    : null,
                )}
                recipientCount={recipients.length}
                students={recipients}
                certificateNames={certificateNamesByStudent}
                grades={gradesByStudent}
                canPreview={canPreview}
                canConfirm={previewIsCurrent && !batchRunning && !batch}
                onNameChange={handleCertificateNameChange}
                onGradeChange={handleGradeChange}
                onRemoveRecipient={handleRemoveRecipient}
                onConfirm={handleGenerateForReview}
              />
            </fieldset>

            <CertificateBatchProgress
              batch={batch}
              running={batchRunning}
              error={batchError}
              onResume={resumeBatch}
              onRetry={retryFailedBatch}
              onPublish={handlePublishBatch}
              onCancel={handleCancelBatch}
              onCancelIssuance={handleCancelDraftIssuance}
              onClear={handleCreateAnotherBatch}
            />

            <CertificateDraftBatchList
              activeBatchId={batch?.id_lote}
              refreshKey={draftListRefreshKey}
              onSelect={handleLoadBatch}
            />

            {wizardError && (
              <p className="certificate-wizard-status" role="alert">
                {wizardError}
              </p>
            )}
            {!wizardError &&
              wizardForm.id_clase &&
              !loadingStudents &&
              classStudents.length === 0 && (
                <p className="certificate-wizard-status" role="status">
                  Esta clase no tiene alumnos inscritos.
                </p>
              )}
          </form>
        </section>

        <CertificateManualUpload
          students={manualStudents}
          uploadUrl={subirCertificado}
          loadingStudents={cargandoAlumnos}
          loadError={manualLoadError}
        />
      </main>
    </div>
  );
};
